import { z } from 'zod';
import { ValidationError } from './errors/index.js';
import { Injection } from './injection/Injection.js';
import { Result } from './result/Result.js';
// ============================================================================
// Known Xero sentinel account IDs
// ============================================================================
const XERO_SENTINEL_IDS = new Set(['FXGROUPID', 'FX_CLEARING']);
const GUID_SCHEMA = z.guid();
/**
 * Serialization-layer Zod schema validating account IDs: GUID (any 8-4-4-4-12
 * hex string) or one of the known Xero sentinel literals ("FXGROUPID",
 * "FX_CLEARING").
 *
 * Uses `z.guid()` instead of `z.uuid()` because Xero system accounts (e.g.,
 * "Current Year Earnings") use non-RFC-4122 identifiers that lack valid version
 * bits.
 *
 * The `.brand('AccountId')` produces a Zod-branded type that is structurally
 * distinct from the Flavor-branded domain `AccountId`. The conversion between
 * them happens via `accountIdInjection`.
 */
export const AccountIdSchema = z
    .string()
    .refine((val) => GUID_SCHEMA.safeParse(val).success || XERO_SENTINEL_IDS.has(val), {
    message: 'AccountId must be a GUID or a known Xero sentinel (FXGROUPID, FX_CLEARING)',
})
    .brand('AccountId');
/**
 * Serialization-layer Zod schema validating account codes. Xero allows any
 * unique string up to 10 characters as an account code. Validation does not
 * normalize (no trim, no case-fold) — BigQuery returns exact stored strings;
 * any normalization is the caller's responsibility.
 */
export const AccountCodeSchema = z
    .string()
    .min(1, 'Account code must not be empty')
    .max(10, 'Account code must be at most 10 characters')
    .brand('AccountCode');
/**
 * Serialization-layer Zod schema validating account names. Non-empty
 * free-form text up to 255 characters.
 */
export const AccountNameSchema = z
    .string()
    .trim()
    .min(1, 'Account name must not be empty')
    .max(255, 'Account name must be at most 255 characters')
    .brand('AccountName');
/**
 * Serialization-layer Zod schema validating Xero system-account codes:
 * non-empty text. Xero does not publish a closed set, so we only check
 * presence. No normalization — BigQuery returns exact stored strings.
 */
export const SystemAccountSchema = z.string().min(1, 'SystemAccount must not be empty').brand('SystemAccount');
// ============================================================================
// Injection-based converters
// ============================================================================
const accountIdBrand = Injection.brand();
/**
 * Injection between the Flavor-branded `AccountId` domain type and plain
 * `string`. `apply()` strips the brand; `invert()` validates via
 * `AccountIdSchema` and re-brands.
 */
export const accountIdInjection = new Injection((id) => accountIdBrand.apply(id), (s) => {
    const result = AccountIdSchema.safeParse(s);
    if (!result.success) {
        return Result.error(new ValidationError(`Invalid AccountId: ${s}`));
    }
    return accountIdBrand.invert(result.data);
});
const accountCodeBrand = Injection.brand();
/**
 * Injection between the Flavor-branded `AccountCode` domain type and plain
 * `string`. `apply()` strips the brand; `invert()` validates via
 * `AccountCodeSchema` and re-brands.
 */
export const accountCodeInjection = new Injection((code) => accountCodeBrand.apply(code), (s) => {
    const result = AccountCodeSchema.safeParse(s);
    if (!result.success) {
        return Result.error(new ValidationError(`Invalid AccountCode: ${s}`));
    }
    return accountCodeBrand.invert(result.data);
});
const systemAccountBrand = Injection.brand();
/**
 * Injection between the Flavor-branded `SystemAccount` domain type and plain
 * `string`. `apply()` strips the brand; `invert()` validates via
 * `SystemAccountSchema` and re-brands.
 */
export const systemAccountInjection = new Injection((sa) => systemAccountBrand.apply(sa), (s) => {
    const result = SystemAccountSchema.safeParse(s);
    if (!result.success) {
        return Result.error(new ValidationError(`Invalid SystemAccount: ${s}`));
    }
    return systemAccountBrand.invert(result.data);
});
const accountNameBrand = Injection.brand();
/**
 * Injection between the Flavor-branded `AccountName` domain type and plain
 * `string`. `apply()` strips the brand; `invert()` validates via
 * `AccountNameSchema` and re-brands.
 */
export const accountNameInjection = new Injection((name) => accountNameBrand.apply(name), (s) => {
    const result = AccountNameSchema.safeParse(s);
    if (!result.success) {
        return Result.error(new ValidationError(`Invalid AccountName: ${s}`));
    }
    return accountNameBrand.invert(result.data);
});
// ============================================================================
// Composed account-fields injection
// ============================================================================
/**
 * Composed injection for the standard account-field triple:
 * `{ accountId, accountCode, accountName }`.
 *
 * `accountCode` is optional — absent or `undefined` values pass through.
 */
export const accountFieldsInjection = Injection.object({
    accountId: accountIdInjection,
    accountCode: Injection.optional(accountCodeInjection),
    accountName: accountNameInjection,
});
/**
 * Like `accountFieldsInjection` but with `accountId` optional.
 * Used for TrialBalanceRow (where accountId is optional) and test fixtures
 * that omit accountId.
 */
export const optionalAccountFieldsInjection = Injection.object({
    accountId: Injection.optional(accountIdInjection),
    accountCode: Injection.optional(accountCodeInjection),
    accountName: accountNameInjection,
});
//# sourceMappingURL=AccountRelatedTypes.js.map